import { Button, Result, Typography } from 'antd';
import { Link } from 'react-router';
import { useCurrentUser } from '../../auth/use-session';
import { isRegionBoundaryRefusal, type AbilityRequirement } from '../../auth/policy';

/**
 * What a refusal looks like (docs/07-implementation-roadmap.md F1.10).
 *
 * **A 403 is not an error toast.** The API returns 403 rather than an empty list
 * when a coordinator reaches outside their region, deliberately, so the boundary
 * is legible (docs/01 §1.1) — a coordinator who asks for a member and is told
 * "no such member" learns the wrong thing about the church's data. Collapsing
 * that into "terjadi kesalahan" throws away the one piece of information the
 * server went out of its way to give.
 *
 * So there are two screens here, and which one shows is decided structurally
 * rather than by parsing the server's message: a coordinator refused something
 * coordinators may generally do has been stopped by the region boundary and
 * nothing else (`isRegionBoundaryRefusal`).
 */
export function AccessDenied({ ability }: { ability: AbilityRequirement }) {
  const user = useCurrentUser();
  const outOfRegion = isRegionBoundaryRefusal(user, ability);

  if (outOfRegion) {
    return (
      <Result
        status={403}
        title="Di luar wilayah Anda"
        subTitle="Data ini milik wilayah lain. Sebagai koordinator wilayah, Anda hanya dapat melihat dan mengubah data jemaat di wilayah Anda sendiri."
        extra={
          <Link to="/">
            <Button type="primary">Kembali ke beranda</Button>
          </Link>
        }
      >
        <Typography.Paragraph type="secondary" style={{ textAlign: 'left' }}>
          Bila data ini seharusnya menjadi tanggung jawab Anda, hubungi kantor gereja — biasanya keluarga yang
          bersangkutan perlu dipindahkan ke wilayah Anda, bukan hak akses Anda yang perlu diubah.
        </Typography.Paragraph>
      </Result>
    );
  }

  return (
    <Result
      status={403}
      title="Anda tidak memiliki akses"
      subTitle="Halaman ini tidak tersedia untuk peran akun Anda. Bila Anda memerlukannya untuk pekerjaan Anda, hubungi administrator gereja."
      extra={
        <Link to="/">
          <Button type="primary">Kembali ke beranda</Button>
        </Link>
      }
    />
  );
}
